import { ClarkBrandMark } from "core/components/ClarkBrandMark/ClarkBrandMark";
import { colors } from "styles/colors";
import { styleAsClass } from "styles/styleAsClass";

const LOGO_SIZE = 60;
/** Gap between the logo edge and the progress stroke. */
const RING_PADDING = 8;
const STROKE_WIDTH = 2;

/** Radius to the center of the stroke. */
const RING_RADIUS = LOGO_SIZE / 2 + RING_PADDING + STROKE_WIDTH / 2;
const RING_SIZE = 2 * (RING_RADIUS + STROKE_WIDTH / 2);

export const BOOTSTRAP_RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export const BOOTSTRAP_MARK_BLUR_PX = 6;
const BOOTSTRAP_MARK_EXIT_MS = 200;
const BOOTSTRAP_OVERLAY_EXIT_DELAY_MS = 100;
const BOOTSTRAP_OVERLAY_EXIT_MS = 200;
/** Starts before the white veil finishes so the app never flashes sharp. */
const BOOTSTRAP_BLUR_EXIT_DELAY_MS = 200;
const BOOTSTRAP_BLUR_EXIT_MS = 200;

/** How long the reveal runs before the host may unmount. */
export const BOOTSTRAP_REVEAL_MS =
  BOOTSTRAP_BLUR_EXIT_DELAY_MS + BOOTSTRAP_BLUR_EXIT_MS;

/**
 * The loader must paint above all app chrome. The fixed-position host owns the
 * stacking context, so it carries this value and the root layer just fills it.
 */
export const BOOTSTRAP_LOADER_Z_INDEX = 9999;

const ROOT = styleAsClass`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OVERLAY = styleAsClass`
  position: absolute;
  inset: 0;
  background: ${colors.WHITE};
  transition: opacity ${String(BOOTSTRAP_OVERLAY_EXIT_MS)}ms ease-out ${String(BOOTSTRAP_OVERLAY_EXIT_DELAY_MS)}ms;

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
    transition-delay: 0ms;
  }
`;

/**
 * Separate from the white veil: an opaque background would paint over its own
 * backdrop-filter result and hide the blur entirely. Kept at a constant radius
 * so only opacity animates, and fading it out crossfades blurred into sharp.
 */
const BLUR_VEIL = styleAsClass`
  position: absolute;
  inset: 0;
  background: transparent;
  transition: opacity ${String(BOOTSTRAP_BLUR_EXIT_MS)}ms ease-out ${String(BOOTSTRAP_BLUR_EXIT_DELAY_MS)}ms;

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
    transition-delay: 0ms;
  }
`;

/**
 * The blur only applies during the reveal. While the white veil is opaque the
 * blur is invisible anyway, but the compositor would still snapshot and blur
 * the full viewport every frame — cold-start critical path. The white veil's
 * exit delay guarantees the blur is in place before anything shows through.
 */
const BLUR_VEIL_ACTIVE = styleAsClass`
  backdrop-filter: blur(${String(BOOTSTRAP_MARK_BLUR_PX)}px);
`;

const MARK_LAYER = styleAsClass`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${String(RING_SIZE)}px;
  height: ${String(RING_SIZE)}px;
  transition:
    filter ${String(BOOTSTRAP_MARK_EXIT_MS)}ms ease-out,
    opacity ${String(BOOTSTRAP_MARK_EXIT_MS)}ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    filter: none !important;
  }
`;

const RING = styleAsClass`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const RING_TRACK = styleAsClass`
  fill: none;
  stroke: ${colors.GREY_100};
  stroke-width: ${String(STROKE_WIDTH)};
`;

/** Must match the ring fill CSS transition so dismiss can wait for 100%. */
export const BOOTSTRAP_RING_TRANSITION_MS = 200;

/** Hold at ready before reveal so the ring can finish animating to 100%. */
export const BOOTSTRAP_READY_HOLD_MS = BOOTSTRAP_RING_TRANSITION_MS;

const RING_FILL = styleAsClass`
  fill: none;
  stroke: ${colors.GREY_900};
  stroke-width: ${String(STROKE_WIDTH)};
  stroke-linecap: round;
  transition: stroke-dashoffset ${String(BOOTSTRAP_RING_TRANSITION_MS)}ms linear;

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
  }
`;

function clampProgress(progress: number): number {
  if (Number.isNaN(progress)) {
    return 0;
  }
  return Math.min(1, Math.max(0, progress));
}

export function FullPageBootstrapLoader({
  exiting = false,
  progress,
}: {
  exiting?: boolean;
  progress: number;
}) {
  const clamped = clampProgress(progress);
  const dashOffset = BOOTSTRAP_RING_CIRCUMFERENCE * (1 - clamped);
  // Exit transitions are one-way. Cancelling a reveal must restore the opaque
  // loader immediately, or the app stays visible under a see-through overlay
  // for the length of the exit (including its delay).
  const exitTransition = exiting ? undefined : "none";

  return (
    <div
      className={ROOT}
      data-test="full-page-bootstrap-loader"
      style={{ pointerEvents: "none" }}
    >
      <div
        className={exiting ? `${BLUR_VEIL} ${BLUR_VEIL_ACTIVE}` : BLUR_VEIL}
        data-test="bootstrap-blur-veil"
        aria-hidden="true"
        style={{ opacity: exiting ? 0 : 1, transition: exitTransition }}
      />
      <div
        className={OVERLAY}
        data-test="bootstrap-overlay"
        aria-hidden="true"
        style={{ opacity: exiting ? 0 : 1, transition: exitTransition }}
      />
      <div
        className={MARK_LAYER}
        data-test="bootstrap-progress-bar"
        style={{
          filter: exiting
            ? `blur(${String(BOOTSTRAP_MARK_BLUR_PX)}px)`
            : "blur(0px)",
          opacity: exiting ? 0 : 1,
          transition: exitTransition,
        }}
      >
        {/* Progressbar semantics live on the ring itself: putting
            role="progressbar" on the parent layer would make the nested brand
            mark presentational and prune its "Clark AI" label from the
            accessibility tree. */}
        <svg
          className={RING}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          role="progressbar"
          aria-label="Loading Superblocks"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(clamped * 100)}
        >
          <circle
            className={RING_TRACK}
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
          />
          <circle
            className={RING_FILL}
            data-test="bootstrap-progress-fill"
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            strokeDasharray={BOOTSTRAP_RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <ClarkBrandMark />
      </div>
    </div>
  );
}
